import React, { useEffect, useMemo, useState } from 'react';
import { load_takedown_sla_metrics, load_takedowns_list } from '../../services';
import CommonLoader from '../../Components/CommonLoader';

import CommonTable from '../../Components/CommonTable';
import CommonPagination from '../../Components/CommonPagination';
import NoDataPage from '../../Components/NoDataPage';
import StatCard from '../../Components/StatCard';
import StatusBadge from '../../Components/StatusBadge';
import { Body, SubHeading } from '../../Constants/Fonts';
import ModuleIcon from '../../Components/ModuleIcon';
import './TakedownSlaWidget.scss';

function unwrap(resp) {
    return resp?.statusCode?.response_data?.data
        ?? resp?.statusCode
        ?? resp?.data
        ?? resp
        ?? {};
}

function fmtPct(v) {
    if (v == null) return '-';
    return `${Number(v).toFixed(0)}%`;
}


function fmtHours(h) {
    if (h == null) return '-';
    if (h < 1)  return `${Math.round(h * 60)}m`;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
}

function classify({ met, breached, pending, target }) {
    const evaluable = met + breached;
    if (evaluable === 0) return pending > 0 ? 'pending' : 'neutral';
    const pct = (met / evaluable) * 100;
    if (pct >= target) return 'ok';
    if (pct >= target - 15) return 'warn';
    return 'bad';
}

function statusLabel(s) {
    if (s === 'resolved')  return 'Resolved';
    if (s === 'initiated') return 'Filed';
    if (s === 'requested') return 'Awaiting filing';
    if (s === 'failed')    return 'Failed';
    return s || 'Unknown';
}

function statusVariant(s) {
    if (s === 'resolved')  return 'success';
    if (s === 'failed')    return 'danger';
    if (s === 'initiated') return 'info';
    return 'neutral';
}

function Panel({ title, note, action, children }) {
    return (
        <section className="bm_td_panel cmn_elevated_card">
            {(title || note || action) && (
                <div className="bm_td_panel_head">
                    <div>
                        {title && (
                            <SubHeading as="h3" variant={2} className="bm_td_panel_title">
                                {title}
                            </SubHeading>
                        )}
                        {note && (
                            <Body as="span" variant={2} className="bm_td_muted">{note}</Body>
                        )}
                    </div>
                    {action && <div className="bm_td_panel_action">{action}</div>}
                </div>
            )}
            {children}
        </section>
    );
}

function Pager({ page, pageCount, onChange }) {
    if (pageCount <= 1) return null;
    return (
        <div className="bm_td_pager">
            <CommonPagination
                handlePageClick={({ selected }) => onChange(selected)}
                pageCount={pageCount}
                forcePage={page}
            />
        </div>
    );
}


const TABLE_HEADERS = ['Domain', 'Status', 'Requested', 'Filed', 'Resolved', 'SLA'];
const PAGE_SIZE = 10;

function TakedownSlaWidget() {
    const [data, setData]               = useState(null);
    const [metricsLoading, setMLoading] = useState(true);
    const [items, setItems]             = useState([]);
    const [listLoading, setLLoading]    = useState(true);
    const [page, setPage]               = useState(0);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const resp = await load_takedown_sla_metrics({ days: 30 });
                if (!alive) return;
                setData(unwrap(resp));
            } catch {
                if (alive) setData(null);
            } finally {
                if (alive) setMLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;
        load_takedowns_list({ bucket: '', limit: 200 })
            .then(r => { if (alive) setItems(unwrap(r)?.items || []); })
            .catch(() => alive && setItems([]))
            .finally(() => alive && setLLoading(false));
        return () => { alive = false; };
    }, []);

    const pageCount  = Math.ceil(items.length / PAGE_SIZE);
    const pagedItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const tableRows = useMemo(() => pagedItems.map(it => ({
        item: it,
        data: [
            <Body as="span" variant={2} weight="semibold" className="bm_td_domain">
                {it.domain}
            </Body>,
            <StatusBadge variant={statusVariant(it.status)}>
                {statusLabel(it.status)}
            </StatusBadge>,
            <Body as="span" variant={2} className="bm_td_muted">
                {it.created_date ? new Date(it.created_date).toLocaleDateString() : '-'}
            </Body>,
            <Body as="span" variant={2} className="bm_td_muted">
                {it.initiated_at ? new Date(it.initiated_at).toLocaleDateString() : 'Not yet'}
            </Body>,
            <Body as="span" variant={2} className="bm_td_muted">
                {it.resolved_at ? new Date(it.resolved_at).toLocaleDateString() : 'Pending'}
            </Body>,
            it.sla_initiate_met === true
                ? <StatusBadge variant="success">On time</StatusBadge>
                : it.sla_initiate_met === false
                ? <StatusBadge variant="danger">Breached</StatusBadge>
                : <StatusBadge variant="neutral">In window</StatusBadge>,
        ],
    })), [pagedItems]);

    if (metricsLoading) return (
        <div className="bm_td_loading"><CommonLoader label="Loading takedown data..." /></div>
    );

    if (!data || (data.total_takedowns ?? 0) === 0) return (
        <NoDataPage
            title="No takedown requests"
            hint="Takedowns are filed from the Mentions tab using 'Request takedown' on a mention card. Once filed, SLA tracking appears here."
        />
    );

    const initiateTone = classify({
        met:      data.initiated_within_24h_count || 0,
        breached: data.breached_initiate_count    || 0,
        pending:  data.pending_initiate_count     || 0,
        target: 90,
    });
    const resolveTone = classify({
        met:      data.resolved_within_5d_count || 0,
        breached: data.breached_resolve_count   || 0,
        pending:  data.pending_resolve_count    || 0,
        target: 80,
    });

    const iconTone = t => t === 'bad' ? 'danger' : t === 'warn' ? 'warning' : t === 'ok' ? 'success' : 'primary';

    const totalBreached = (data.breached_initiate_count || 0) + (data.breached_resolve_count || 0);
    const openCount     = data.total_takedowns - (data.total_resolved || 0);

    return (
        <div className="bm_td">
            {totalBreached > 0 && (
                <div className="bm_td_alert">
                    <StatusBadge variant="danger">
                        {totalBreached} {totalBreached === 1 ? 'takedown' : 'takedowns'} missed SLA
                    </StatusBadge>
                    <Body as="span" variant={2}>
                        Review the breached items below and follow up with your provider.
                    </Body>
                </div>
            )}

            <div className="bm_td_kpis">
                <StatCard className="cmn_elevated_card"
                    label="Total takedowns"
                    value={data.total_takedowns}
                    hint={`${data.total_resolved || 0} resolved, ${openCount} open`}
                    icon={<span className="bm_td_kpi_icon"><ModuleIcon name="violation" size={18} color="currentColor" /></span>} iconLeft />
                <StatCard className="cmn_elevated_card"
                    label="Filed on time"
                    value={initiateTone === 'pending'
                        ? `${data.pending_initiate_count} open`
                        : fmtPct(data.sla_initiate_pct)}
                    hint="target: within 24h of request"
                    icon={<span className={`bm_td_kpi_icon is-${iconTone(initiateTone)}`}><ModuleIcon name="history" size={18} color="currentColor" /></span>} iconLeft />
                <StatCard className="cmn_elevated_card"
                    label="Resolved on time"
                    value={resolveTone === 'pending'
                        ? `${data.pending_resolve_count} open`
                        : fmtPct(data.sla_resolve_pct)}
                    hint="target: within 5 days"
                    icon={<span className={`bm_td_kpi_icon is-${iconTone(resolveTone)}`}><ModuleIcon name="check_circle" size={18} color="currentColor" /></span>} iconLeft />
                <StatCard className="cmn_elevated_card"
                    label="Avg. resolution"
                    value={fmtHours(data.avg_resolve_hours)}
                    hint={`across ${data.total_resolved || 0} resolved`}
                    icon={<span className="bm_td_kpi_icon"><ModuleIcon name="compliance" size={18} color="currentColor" /></span>} iconLeft />
            </div>

            <Panel
                title="Takedown requests"
                note={`last ${data.window_days || 30} days`}>
                {listLoading ? (
                    <div className="bm_td_loading"><CommonLoader /></div>
                ) : items.length === 0 ? (
                    <NoDataPage
                        title="No takedown records"
                        hint="No takedowns have been filed yet."
                    />
                ) : (
                    <>
                        <CommonTable
                            headers={TABLE_HEADERS}
                            rows={tableRows}
                            noScroll
                        />
                        <Pager page={page} pageCount={pageCount} onChange={p => { setPage(p); }} />
                    </>
                )}
            </Panel>

        </div>
    );
}

export default TakedownSlaWidget;
